import { useRef, useState, type ChangeEvent, Fragment } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api/client";
import { usePdfPasswordPrompt } from "@/hooks/usePdfPasswordPrompt";
import {
  deleteApplicationDocumentFile,
  listApplicationDocuments,
  uploadApplicationDocument,
} from "@/services/documents";
import type { DocumentChecklistItem } from "@/types/application";

type DocumentUploadPanelProps = {
  applicationUuid: string;
  checklist: DocumentChecklistItem[];
  title: string;
  description?: string;
  businessTypeId?: number | null;
  shareholderUuid?: string;
  emptyMessage?: string;
  readOnly?: boolean;
  embedded?: boolean;
  onUploaded: () => Promise<void> | void;
};

export function DocumentUploadPanel({
  applicationUuid,
  checklist = [],
  title,
  description,
  businessTypeId,
  shareholderUuid,
  emptyMessage = "No documents in this section.",
  readOnly = false,
  embedded = false,
  onUploaded,
}: DocumentUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeItem, setActiveItem] = useState<DocumentChecklistItem | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [deletingFileUuid, setDeletingFileUuid] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const { uploadFileWithPasswordPrompt, renderPasswordModal } = usePdfPasswordPrompt();

  const openPicker = (item: DocumentChecklistItem) => {
    if (readOnly) return;
    setActiveItem(item);
    setError(null);
    inputRef.current?.click();
  };

  const toggleFiles = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const item = activeItem;
    event.target.value = "";

    if (!files.length || !item) return;
    if (!item.document_type_id) {
      setError("Missing document type id for upload.");
      return;
    }

    setUploadingKey(item.document_key);
    setError(null);
    try {
      let documentUuid = item.document_uuid ?? null;
      for (const file of files) {
        await uploadFileWithPasswordPrompt(file, (pdfPassword) =>
          uploadApplicationDocument(applicationUuid, {
            documentTypeId: item.document_type_id!,
            file,
            businessTypeId,
            shareholderUuid,
            documentUuid,
            pdfPassword,
          }),
        );
        if (!documentUuid) {
          const refreshed = await listApplicationDocuments(applicationUuid);
          documentUuid =
            refreshed.find((d) => d.document_type === item.document_type_id)?.uuid ?? null;
        }
      }
      setExpandedKeys((prev) => ({ ...prev, [item.document_key]: true }));
      await onUploaded();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload failed. Try again.";
      setError(message);
    } finally {
      setUploadingKey(null);
      setActiveItem(null);
    }
  };

  const onDeleteFile = async (item: DocumentChecklistItem, fileUuid: string) => {
    if (readOnly) return;
    if (!item.document_uuid) {
      setError("Missing document id for delete.");
      return;
    }

    setDeletingFileUuid(fileUuid);
    setError(null);
    try {
      await deleteApplicationDocumentFile(applicationUuid, {
        documentUuid: item.document_uuid,
        documentFileUuid: fileUuid,
      });
      await onUploaded();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Delete failed. Try again.";
      setError(message);
    } finally {
      setDeletingFileUuid(null);
    }
  };

  const Wrapper = embedded ? "div" : "section";

  return (
    <>
      {renderPasswordModal()}

      <Wrapper
        className={
          embedded
            ? undefined
            : "rounded-2xl border border-line bg-surface-raised p-5 sm:p-6"
        }
      >
        <div className="mb-5">
          <h2 className={embedded ? "text-base font-semibold text-ink" : "text-lg font-semibold text-ink"}>
            {title}
          </h2>
          {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
        </div>

        {error ? <Alert className="mb-4">{error}</Alert> : null}

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          multiple
          onChange={(e) => void onFileChange(e)}
        />

        {checklist.length === 0 ? (
          <p className="text-sm text-muted">{emptyMessage}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="px-2 py-2 font-medium">Document name</th>
                  <th className="px-2 py-2 font-medium">Files</th>
                  {readOnly ? null : <th className="px-2 py-2 font-medium">Action</th>}
                </tr>
              </thead>
              <tbody>
                {checklist.map((item) => {
                  const rowKey = `${shareholderUuid ?? "app"}-${item.document_type_id ?? item.document_key}`;
                  const files = item.files ?? [];
                  const expanded = Boolean(expandedKeys[item.document_key]);
                  const uploadLabel = files.length ? "Add files" : "Upload";

                  return (
                    <Fragment key={rowKey}>
                      <tr className="border-b border-line align-top">
                        <td className="px-2 py-3">
                          <p className="font-medium text-ink">
                            {item.label}
                            {item.required ? <span className="text-danger"> *</span> : null}
                          </p>
                          {item.description ? (
                            <p className="mt-1 text-sm text-muted">{item.description}</p>
                          ) : item.multi_upload ? (
                            <p className="mt-0.5 text-xs text-muted">Multiple uploads allowed</p>
                          ) : null}
                        </td>
                        <td className="px-2 py-3">
                          {files.length > 0 ? (
                            <button
                              type="button"
                              className="text-sm font-medium text-brand hover:underline"
                              onClick={() => toggleFiles(item.document_key)}
                            >
                              {expanded ? "Hide files" : `Show files (${files.length})`}
                            </button>
                          ) : (
                            <span className="text-muted">No files</span>
                          )}
                        </td>
                        {readOnly ? null : (
                          <td className="px-2 py-3">
                            <Button
                              type="button"
                              variant={files.length ? "secondary" : "primary"}
                              size="sm"
                              loading={uploadingKey === item.document_key}
                              onClick={() => openPicker(item)}
                            >
                              {uploadLabel}
                            </Button>
                          </td>
                        )}
                      </tr>
                      {expanded && files.length > 0 ? (
                        <tr className="border-b border-line bg-surface">
                          <td colSpan={readOnly ? 2 : 3} className="px-2 py-3">
                            <ul className="space-y-2 rounded-xl border border-line p-3">
                              {files.map((file) => (
                                <li
                                  key={file.uuid}
                                  className="flex flex-wrap items-center justify-between gap-2"
                                >
                                  <div className="min-w-0 flex-1">
                                    {file.file_url ? (
                                      <a
                                        href={file.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="break-all font-medium text-brand hover:underline"
                                      >
                                        {file.file_name}
                                      </a>
                                    ) : (
                                      <span className="break-all text-ink">{file.file_name}</span>
                                    )}
                                  </div>
                                  {readOnly ? null : (
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      size="sm"
                                      loading={deletingFileUuid === file.uuid}
                                      onClick={() => onDeleteFile(item, file.uuid)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Wrapper>
    </>
  );
}
